import React from 'react';
import { Flex, Text, FlexProps } from '@mantine/core';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	extractDecimalPrecisionFromUnitDenominator,
	formatNumberToUI,
} from '@dealstack/shared/lib/format/number.ts';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string.ts';
import styled from 'styled-components';
import { CurrencyType, SecurityIssuedIn } from '@dealstack/shared/lib/generated';
import { isSecurityIssuedInUnits } from '@dealstack/frontend/shared/cap.ts';

interface Props {
	date: Date | null;
	transactionType: string | null;
	units?: number;
	transactionCost?: number;
	currency?: CurrencyType;
	securityIssuedIn: SecurityIssuedIn;
	securityUnitDenominator: number;
	width?: React.CSSProperties['width'];
}

const BLOCK_SUMMARY_PADDING = '1.5rem';

export const BlockSummary = ({
	date,
	transactionType,
	units,
	width,
	transactionCost,
	currency,
	securityIssuedIn,
	securityUnitDenominator,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const formattedDate = date ? formatDateToUI(date, DATE_FORMATS.CALENDAR_DATE) : null;
	const formattedTime = date ? formatDateToUI(date, DATE_FORMATS.TIME) : null;
	const precision = extractDecimalPrecisionFromUnitDenominator(securityUnitDenominator);
	const unitsText = units
		? `${formatNumberToUI(units, undefined, precision)} ${tCommonUi('units')}`
		: '';
	const costText = transactionCost ? formatNumberToUI(transactionCost, currency) : '';

	const securitySummaryText = `${normalizeScreamingSnakeToUI(transactionType)} ${isSecurityIssuedInUnits(securityIssuedIn) ? unitsText : costText}`;
	return (
		<Flex
			px={BLOCK_SUMMARY_PADDING}
			gap="1rem"
			align="stretch"
			w={width}
			style={{ boxSizing: 'border-box' }}
		>
			<StyledTimeContainer
				py={BLOCK_SUMMARY_PADDING}
				direction="column"
				align="center"
				justify={date ? undefined : 'center'}
				gap="0.25rem"
			>
				{formattedDate && <Text fz="md">{formattedDate}</Text>}
				{formattedTime && (
					<Text fz="sm" c="dark.0" lh="1.5em">
						{formattedTime}
					</Text>
				)}
				{!date ? (
					<Text fs="italic" fz="sm" c="dark.0">
						{tCommonUi('no_data')}
					</Text>
				) : null}
			</StyledTimeContainer>
			<Flex py={BLOCK_SUMMARY_PADDING} direction="column" gap="0.25rem">
				<Text fz="md" fw={600}>
					{securitySummaryText}
				</Text>
			</Flex>
		</Flex>
	);
};

const StyledTimeContainer = styled(Flex)<FlexProps>`
	position: relative;
	min-width: 83px;

	::before,
	::after {
		content: '';
		height: ${BLOCK_SUMMARY_PADDING};
		background-color: ${({ theme }) => theme.v3.colors.gray[9]};
		width: 1px;
		position: absolute;
		left: 50%;
		right: 50%;
		z-index: 1;
	}

	::before {
		top: 0;
	}

	::after {
		bottom: 0;
	}
`;
